// packages
import { create } from 'zustand'

// libs
import { api } from '@/lib/axios'

// entities
import {
  ExamAnalysisProps,
  ExamCollection,
  ExamMarkerValues,
  exam_analysis_category,
  exam_analysis_trimester,
  exam_analysis_children_range
} from '@/entities/exam-analysis'

// constants
import { HTTP_STATUS_CODE } from '@/constants/http'

// store
import { useAuthStore } from './auth'

export interface ExamAnalysisState {
  items: ExamAnalysisProps[]
  isLoading: boolean
  fetchAll: () => Promise<void>
  fetchByRef: (ref: string) => Promise<ExamAnalysisProps | null>
  create: (input: CreateInput) => Promise<ExamAnalysisProps | null>
  update: (ref: string, input: Partial<CreateInput>) => Promise<ExamAnalysisProps | null>
  remove: (ref: string) => Promise<boolean>
  generateAnalysis: (ref: string) => Promise<{ ref: string; historyRef: string } | { error: string } | null>
  setLocal: (record: ExamAnalysisProps) => void
}

export interface CreateInput {
  patientName: string
  category: exam_analysis_category
  trimester?: exam_analysis_trimester | null
  childrenRange?: exam_analysis_children_range | null
  gestationalWeeks?: number | null
  gestationalDays?: number | null
  collections?: ExamCollection[]
  markerValues?: ExamMarkerValues
  /**
   * Metadados ricos por marcador (nome, range, classificação por coleta).
   * Necessário para o backend enriquecer o prompt da IA — sem isso a Nina
   * recebe só ID+valor e pode classificar errado.
   */
  markersContext?: Record<string, {
    name: string
    group?: string
    unit?: string
    range?: { display?: string; min?: number; max?: number }
    byCollection?: Record<string, { value: string; status?: string }>
  }>
}

const userRef = () => useAuthStore.getState().user?.ref

export const useExamAnalysisStore = create<ExamAnalysisState>()((set, get) => ({
  items: [],
  isLoading: false,

  fetchAll: async () => {
    const ref = userRef()
    if (!ref) return
    set({ isLoading: true })
    try {
      const response = await api.get('/', { params: { action: 'exam-analysis', userRef: ref, page: 0, limit: 50 } })
      if (response.data.statusCode === HTTP_STATUS_CODE.OK) {
        const body = response.data.body
        const items: ExamAnalysisProps[] = Array.isArray(body?.[0]) ? body[0] : []
        set({ items })
      }
    } finally {
      set({ isLoading: false })
    }
  },

  fetchByRef: async ref => {
    const userRefVal = userRef()
    if (!userRefVal) return null
    const response = await api.get('/', { params: { action: 'exam-analysis-by-ref', userRef: userRefVal, ref } })
    if (response.data.statusCode !== HTTP_STATUS_CODE.OK) return null
    const item: ExamAnalysisProps | undefined = response.data.body?.[0]
    if (!item) return null
    const current = get().items.filter(i => i.ref !== ref)
    set({ items: [item, ...current] })
    return item
  },

  create: async input => {
    const ref = userRef()
    if (!ref) return null
    const response = await api.post('/', {
      action: 'exam-analysis',
      userRef: ref,
      patientName: input.patientName,
      category: input.category,
      trimester: input.trimester ?? null,
      childrenRange: input.childrenRange ?? null,
      gestationalWeeks: input.gestationalWeeks ?? null,
      gestationalDays: input.gestationalDays ?? null,
      collections: input.collections ?? [],
      markerValues: input.markerValues ?? {},
      markersContext: input.markersContext ?? {}
    })
    if (response.data.statusCode !== HTTP_STATUS_CODE.CREATED) return null
    const created: { ref: string } | undefined = response.data.body?.[0]
    if (!created?.ref) return null
    return await get().fetchByRef(created.ref)
  },

  update: async (ref, input) => {
    const userRefVal = userRef()
    if (!userRefVal) return null
    const response = await api.put('/', {
      action: 'exam-analysis',
      userRef: userRefVal,
      ref,
      ...input
    })
    if (response.data.statusCode !== HTTP_STATUS_CODE.OK) return null
    return await get().fetchByRef(ref)
  },

  remove: async ref => {
    const userRefVal = userRef()
    if (!userRefVal) return false
    const response = await api.delete('/', { params: { action: 'exam-analysis', userRef: userRefVal, ref } })
    if (response.data.statusCode !== HTTP_STATUS_CODE.OK) return false
    set(state => ({ items: state.items.filter(i => i.ref !== ref) }))
    return true
  },

  generateAnalysis: async ref => {
    const userRefVal = userRef()
    if (!userRefVal) return null
    const response = await api.post('/', {
      action: 'generate-exam-analysis',
      userRef: userRefVal,
      ref
    })
    if (response.data.statusCode === HTTP_STATUS_CODE.CONFLICT) {
      return { error: response.data.statusMessage || 'Operação não permitida.' }
    }
    if (response.data.statusCode !== HTTP_STATUS_CODE.CREATED) {
      return { error: response.data.statusMessage || 'Falha ao gerar análise.' }
    }
    await get().fetchByRef(ref)
    return response.data.body?.[0] ?? null
  },

  setLocal: record => {
    set(state => {
      const idx = state.items.findIndex(i => i.ref === record.ref)
      if (idx === -1) return { items: [record, ...state.items] }
      const arr = [...state.items]
      arr[idx] = record
      return { items: arr }
    })
  }
}))
